/* eslint-disable no-underscore-dangle */
// Poll for whether there has been any site activity every X seconds.
// The server does not get polled if there has been mouse/keyboard input
import axios from 'axios';
import { nextFrame } from 'helpers/timing';

export default class SessionPoller {
  constructor() {
    this.sessionTimeoutLength = 60;
    this.lastActive = new Date().valueOf();
    this.sessionTimeoutPollFrequency = 180;
    this.remainingSession = this.sessionTimeoutLength * 60; // number of seconds for the session timeout set in devise.rb
    this._countdownInterval = null;
    this.countdownTime = this.remainingSession;
    this.modalActive = false;
  }

  set countdownInterval(val) {
    this._countdownInterval = val;
  }

  get countdownInterval() {
    return this._countdownInterval;
  }

  init = async () => {
    let env;
    let currentUser;
    let remaining;

    await this.sendKeepAliveRequest().then((response) => {
      env = response.data.env;
      currentUser = response.data.current_user;
      remaining = response.data.remaining;
    });
    // Assume this page is public and the user is not logged in
    if (remaining === 0 && !currentUser) return;
    if (env === 'development') return;
    ['mousemove', 'scroll', 'click', 'keydown'].forEach((activity) => {
      document.addEventListener(activity, (ev) => {
        this.lastActive = ev.timeStamp + performance.timing.navigationStart;
      }, { capture: true });
    });
    this.poll();
  };

  poll = () => {
    this.engageCountdown();
    setTimeout(this.pollForSessionTimeout, this.sessionTimeoutPollFrequency * 1000);
  };

  pollForSessionTimeout = () => {
    // Check if they have been active in the session logout duration
    if (Date.now() - this.lastActive < this.sessionTimeoutPollFrequency * 1000 && !this.modalActive) {
      if (this.countdownTime < 30) this.sendKeepAliveRequest();
    } else {
      this.checkTimeoutRequest();
    }
    this.poll();
  };

  checkTimeoutRequest = () => {
    // in development 304 are treated like errors by axios, this negates that
    axios.get('/check_session_timeout', {
      validateStatus: status => status === 304 || (status >= 200 && status < 300)
    }).then(async (response) => {
      // if the remaining valid time for the current user session is less than or equals to 0 seconds.
      if (response.status === 200 && response.data <= 0) {
        document.body.dispatchEvent(new CustomEvent('session-timeout', { bubbles: true, cancelable: false }));
        // Give some time for the `session-timeout` listeners to process the event.
        await nextFrame();
        window.location.href = `/session_timeout?rd=${encodeURIComponent(window.location.pathname)}`;
      } else {
        this.remainingSession = response.data;
        this.countdownTime = response.data;
        this.engageCountdown();
        // If they have not been active and it's less than 360 seconds, show them a message
        if (this.remainingSession < 360 && !this.modalActive) {
          this.handleMessage();
        } else if (this.remainingSession > 360 && this.modalActive) {
          // If timeout is reset then close the modal
          $('#sessionExpiringWarning').modal('hide');
        }
      }
    }).catch(async (error) => {
      if(error.response.status === 401) {
        // we are going to assume that if this session poller catches a 401 its because the user has been signed out.
        await nextFrame();
        window.location.href = `/session_timeout?rd=${encodeURIComponent(window.location.pathname)}`;
      }
    });
  };

  engageCountdown = () => {
    if (!this.countdownInterval) this.countdownInterval = setInterval(this.updateTimeRemainingMessage, 1000);
  };

  sendKeepAliveRequest = () =>
    new Promise((resolve) => {
      if (this.modalActive) return;

      axios.get('/ka').then((response) => {
        this.remainingSession = response.data.remaining;
        this.countdownTime = response.data.remaining;
        resolve(response);
      });
    });

  updateTimeRemainingMessage = () => {
    this.countdownTime -= 1;
    if (this.countdownTime < 0) {
      this.checkTimeoutRequest();
      return;
    }
    if (!document.getElementById('remainingTime')) return;
    if (this.countdownTime <= 10) {
      document.getElementById('remainingTime').classList.add('text-danger');
    }
    document.getElementById('remainingTime').textContent = `${this.countdownTime} second${
      this.countdownTime === 1 ? '' : 's'
    }`;
  };

  handleMessage = () => {
    if (!document.getElementById('sessionExpiringWarning')) {
      const msg = `
          <div class="modal fade" id="sessionExpiringWarning" tabindex="-1" role="dialog" aria-labelledby="sessionExpiringWarning" aria-hidden="true">
            <div class="modal-dialog" role="document">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="sessionExpiringWarningLabel">You will be logged out in...</h5>
                  <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body">
                  <h1 id="remainingTime" class="text-center mt-0">${this.countdownTime} seconds</h1>
                  <p class="fs-3"><em>Ambiki automatically signs you out after 60 minutes of inactivity to keep your data secure.</em></p>
                </div>
                <div class="modal-footer">
                  <a class="btn btn-light" itemprop="url" rel="nofollow" data-method="delete" href="/signout">
                    <i class="bi bi-box-arrow-right"></i> Sign out now</a>
                  <button type="button" class="btn btn-primary" data-dismiss="modal">Stay logged in</button>
                </div>
              </div>
            </div>
          </div>`;
      document.body.insertAdjacentHTML('beforeend', msg);
      if (typeof $('#sessionExpiringWarning').modal === 'function') {
        $('#sessionExpiringWarning').on('show.bs.modal', () => {
          this.modalActive = true;
          document.getElementById('remainingTime').textContent = '...';
        });
        $('#sessionExpiringWarning').on('hidden.bs.modal', () => {
          this.modalActive = false;
          this.sendKeepAliveRequest();
          document.getElementById('remainingTime').classList.remove('text-danger');
        });
      }
    }
    $('#sessionExpiringWarning').modal('show');
  };
}
